import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { confirmForgotPasswordWithCode } from '../../store/auth/authActions'
import { selectAuthErrors, selectForgotPasswordStatus } from '../../store/auth/authSlice'
import ZocksLogo from '../../assets/images/zocks-logo-black.svg'
import { PasswordForm } from './PasswordForm'
import { Button } from 'primereact/button'
import './Auth.scss'

const ResetPasswordViewV2: React.FC = () => (
  <div className='auth-container'>
    <ResetPasswordHeader />
    <ResetPassword />
  </div>
)

export default ResetPasswordViewV2

const ResetPasswordHeader: React.FC = () => (
  <div className='auth-header'>
    <img className='auth-logo' src={ZocksLogo} alt='Zocks logo' />
    <div className='auth-header-text'>
      <div>Already have an account?</div>
      <div className='auth-header-link'>
        <Link to={'/login'}>Log in</Link>
      </div>
    </div>
  </div>
)

const ResetPassword: React.FC = () => {
  return (
    <div className='auth-popup-container'>
      <div className='auth-popup-inner'>
        <h2 className='auth-popup-header'>Update password</h2>
        <ResetPasswordForm />
      </div>
    </div>
  )
}

type ResetPasswordFormValues = {
  password: string
  confirmPassword?: string
}

const ResetPasswordForm: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const authErrors = useAppSelector(selectAuthErrors)
  const status = useAppSelector(selectForgotPasswordStatus)

  const email = searchParams.get('email') || ''
  const code = searchParams.get('code') || ''

  const form = useForm<ResetPasswordFormValues>({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    reValidateMode: 'onSubmit',
  })
  const { handleSubmit } = form

  const errorMessage = authErrors?.find((error) => error.message)?.message

  const onSubmit = async (data: ResetPasswordFormValues) => {
    try {
      const { password } = data
      dispatch(confirmForgotPasswordWithCode({ email, password, code }))
    } catch (e) {
      console.error(e)
    }
  }

  if (!email || !code) {
    return <div>Invalid password reset URL.</div>
  }

  return (
    <FormProvider {...form}>
      <form className='reset-password-form-container' method='post' onSubmit={handleSubmit(onSubmit)}>
        <PasswordForm loading={status === 'loading'} passwordLabel='New Password' />
        {errorMessage && <div className='p-error'>{errorMessage}</div>}
        {status === 'succeeded' ? (
          <Button className='auth-email-button forgot-password-button-success' type='button' onClick={() => navigate('/login')}>
            <span className='auth-email-button-text'>New password set! Back to login…</span>
          </Button>
        ) : (
          <Button className='auth-email-button' type='submit' loading={status === 'loading'}>
            <span className='auth-email-button-text'>Set new password</span>
          </Button>
        )}
      </form>
    </FormProvider>
  )
}
